import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Satellite, Antenna, ScanEye, Monitor, BatteryCharging, ShieldCheck } from "lucide-react";
import { MaskedLines, FadeUp, Eyebrow, EASE, SectionHead } from "@/components/Reveal";
import DemoForm from "@/components/DemoForm";
import ProductSchema from "@/components/ProductSchema";

const HERO_IMG =
  "https://images.unsplash.com/photo-1628158088791-89567a8e84ec?crop=entropy&cs=srgb&fm=jpg&q=85";

const CHIPS = ["8 mm + 1 ppm H / 15 mm V", "1408-channel GNSS", "Calibration-free tilt", "IP67 · MIL-STD-810H"];

const FEATURES = [
  {
    icon: Satellite,
    title: "1408-channel GNSS",
    body: (
      <>
        CHCNAV&apos;s{" "}
        <Link to="/precision-solutions" data-testid="rover-istar-chcnav-link" className="text-signal underline decoration-signal/40 underline-offset-4 transition-colors hover:text-neon">
          iStar2.0 engine
        </Link>{" "}
        tracks GPS, GLONASS, Galileo, BeiDou, QZSS, NavIC and SBAS — reliable fixes measured in millimetres, even in tough environments.
      </>
    ),
  },
  { icon: Antenna, title: "Connected everywhere", body: "Wi-Fi, Bluetooth, NFC, UHF and a 4G modem built in — every controller and correction source stays connected, no dongles." },
  { icon: ScanEye, title: "Calibration-free tilt", body: "The 200 Hz AUTO-IMU measures at up to 60° of pole tilt with E-Bubble levelling — up to 30% faster surveys, no calibration routine, immune to magnetic interference." },
  { icon: Monitor, title: "Status at a glance", body: 'A 1.1" colour OLED plus LEDs and two buttons — satellite count, RTK state and battery without opening a menu.' },
  { icon: BatteryCharging, title: "34-hour battery", body: "9,900 mAh built in — up to 34 h as an RTK rover or 16 h as a base. Survives a 2 m pole drop." },
  { icon: ShieldCheck, title: "Built for the site", body: "IP67-sealed magnesium alloy body, MIL-STD-810H rated for shock and vibration — dust, rain and the ute tray." },
];

const USE_CASES = ["Topographic survey", "Construction layout", "Excavation control", "As-built survey"];

const SPECS = [
  { k: "RTK", v: "8 mm + 1 ppm H / 15 mm + 1 ppm V · <10 s init · >99.9% reliability" },
  { k: "PPP", v: "B2b-PPP / E6B-HAS · 100 mm H / 200 mm V" },
  { k: "GNSS", v: "1408 channels · iStar2.0 · GPS, GLONASS, Galileo, BeiDou, QZSS, NavIC, SBAS" },
  { k: "IMU", v: "200 Hz AUTO-IMU · calibration-free · 0–60° tilt · E-Bubble" },
  { k: "Connectivity", v: "Wi-Fi · Bluetooth 5.0 · NFC · UHF 410–470 MHz Rx/Tx · 4G LTE" },
  { k: "Radio range", v: "3–5 km typical · up to 15 km line-of-sight" },
  { k: "Protocols", v: "RTCM 2.x/3.x · CMR · NMEA 0183 · RINEX · NTRIP client/caster" },
  { k: "Physical", v: "1.15 kg · Ø152 × 78 mm · IP67 · MIL-STD-810H · 2 m pole drop" },
  { k: "Battery", v: "9,900 mAh — up to 34 h RTK rover · 16 h RTK base" },
  { k: "Operating temp", v: "−40°C to +65°C" },
];

const PRODUCT_SCHEMA = {
  "@type": "Product",
  name: "CHCNAV i83 — IMU-RTK GNSS Survey Rover",
  image: HERO_IMG,
  description:
    "CHCNAV i83 IMU-RTK GNSS survey rover — 1408 channels, 8 mm + 1 ppm RTK accuracy, calibration-free 60° tilt compensation, 34-hour battery and built-in UHF and 4G. Supplied and supported across Australia by Green Grade.",
  sku: "CHCNAV-i83",
  model: "i83",
  category: "GNSS survey equipment",
  brand: { "@type": "Brand", name: "CHCNAV" },
  manufacturer: { "@type": "Organization", name: "CHCNAV" },
  url: "https://greengrade.au/products/rover",
};

export default function ProductRover() {
  return (
    <main>
      <ProductSchema product={PRODUCT_SCHEMA} />
      <section className="relative flex min-h-[88svh] items-end overflow-hidden" data-testid="product-rover-hero">
        <motion.div
          initial={{ scale: 1.12 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1.6, ease: EASE }}
          className="absolute inset-0"
        >
          <img src={HERO_IMG} alt="Surveyor using a modern GNSS rover on site" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-ink/75" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/30 to-ink/70" />
        </motion.div>
        <div className="absolute inset-0 bg-blueprint" />
        <div className="relative mx-auto w-full max-w-[1600px] px-4 pb-20 pt-32 sm:px-8 lg:px-16">
          <FadeUp y={12}>
            <Eyebrow>
              <span>
                <Link to="/precision-solutions" data-testid="rover-eyebrow-chcnav-link" className="underline decoration-neon/40 underline-offset-4 transition-colors hover:text-white">
                  CHCNAV
                </Link>{" "}
                i83
              </span>
            </Eyebrow>
          </FadeUp>
          <MaskedLines
            as="h1"
            delay={0.15}
            lines={["Smart IMU-RTK.", { text: "Robustly connected.", lineClass: "italic text-neon" }]}
            className="mt-4 font-display text-6xl font-black uppercase leading-[0.9] tracking-tight text-white sm:text-8xl lg:text-9xl"
          />
          <p className="mt-8 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg">
            The <Link to="/precision-solutions" data-testid="rover-hero-chcnav-link" className="text-signal underline decoration-signal/40 underline-offset-4 transition-colors hover:text-neon">CHCNAV</Link> i83 pairs 1408-channel GNSS with a
            calibration-free 200 Hz IMU — RTK in under 10 seconds, tilt up to 60°, and Wi-Fi, Bluetooth, UHF and 4G
            built in so every correction source stays connected.
          </p>
          <div className="mt-8 flex flex-wrap gap-2">
            {CHIPS.map((c) => (
              <span key={c} className="border border-signal/40 bg-ink/60 px-4 py-2 font-mono text-[11px] uppercase tracking-[0.2em] text-neon backdrop-blur-sm">
                {c}
              </span>
            ))}
          </div>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link
              to="/contact"
              data-testid="product-rover-demo-cta"
              className="group inline-flex items-center gap-3 bg-brand px-8 py-4 font-display text-lg font-bold uppercase tracking-wide text-cream transition-colors hover:bg-signal hover:text-deep"
            >
              Request a demo
              <ArrowUpRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>
            <Link
              to="/#quote"
              data-testid="product-rover-quote-cta"
              className="inline-flex items-center gap-3 border border-signal/50 px-8 py-4 font-display text-lg font-bold uppercase tracking-wide text-neon transition-colors hover:bg-signal/10"
            >
              Build a quote
            </Link>
            <Link
              to="/products/3d"
              data-testid="product-rover-to-3d-link"
              className="inline-flex items-center gap-3 border border-line px-8 py-4 font-display text-lg font-bold uppercase tracking-wide text-white transition-colors hover:border-signal hover:text-neon"
            >
              Feed it to the 3D system
            </Link>
          </div>
        </div>
      </section>

      <section className="px-4 py-20 sm:px-8 lg:px-16 lg:py-32" data-testid="product-rover-features">
        <div className="mx-auto max-w-[1600px]">
          <SectionHead kicker="What it does" title="Surveying, minus the tripod time" body="One receiver that stays connected and never needs levelling — survey, stakeout and site control in a single device." />
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {FEATURES.map((f, i) => (
              <FadeUp key={f.title} delay={i * 0.06}>
                <div className="h-full border border-line bg-surface p-6 transition-colors duration-300 hover:border-signal/50 sm:p-8">
                  <f.icon className="h-6 w-6 text-signal" />
                  <h3 className="mt-5 font-display text-xl font-bold uppercase tracking-tight text-white">{f.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-fog">{f.body}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <section className="border-y border-line bg-deep px-4 py-20 sm:px-8 lg:px-16 lg:py-28" data-testid="product-rover-usecases">
        <div className="mx-auto max-w-[1600px]">
          <SectionHead kicker="Where it earns" title="One rover, every survey" />
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {USE_CASES.map((u, i) => (
              <FadeUp key={u} delay={i * 0.08}>
                <div className="border border-line bg-surface p-8 transition-colors hover:border-signal/50">
                  <span className="text-stroke font-display text-5xl font-black">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="mt-4 font-display text-xl font-bold uppercase text-white">{u}</h3>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-line bg-deep px-4 py-20 sm:px-8 lg:px-16 lg:py-32" data-testid="product-rover-specs">
        <div className="mx-auto max-w-[1600px]">
          <SectionHead kicker="Technical" title="Specifications" />
          <div className="mt-12">
            {SPECS.map((s, i) => (
              <FadeUp key={s.k} delay={i * 0.04} y={16}>
                <div className="flex flex-col gap-1 border-b border-line py-5 sm:flex-row sm:items-baseline sm:justify-between">
                  <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-fog">{s.k}</span>
                  <span className="max-w-[60%] text-sm text-white sm:text-right sm:text-base">{s.v}</span>
                </div>
              </FadeUp>
            ))}
          </div>
          <FadeUp delay={0.1} className="mt-10">
            <Link
              to="/specifications#i83"
              data-testid="product-rover-spec-link"
              className="inline-flex items-center gap-2 border border-line px-6 py-3 font-mono text-[11px] uppercase tracking-[0.25em] text-fog transition-colors hover:border-signal hover:text-neon"
            >
              Full specification breakdown <ArrowUpRight className="h-4 w-4" />
            </Link>
          </FadeUp>
        </div>
      </section>

      <section className="px-4 py-20 sm:px-8 lg:px-16 lg:py-32" data-testid="product-rover-form">
        <div className="mx-auto max-w-[1600px]">
          <SectionHead kicker="Get one on your pole" title="Request an i83 demo" body="See tilt surveying clear a point without levelling the pole — on your site, with your crew holding it." />
          <FadeUp delay={0.12} className="mt-12">
            <DemoForm />
          </FadeUp>
        </div>
      </section>
    </main>
  );
}
